import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { apiError, getInvoicePdf, getInvoices, getPortalSession } from '../../api/account'
import { openCardScreen } from '../../utils/chargebee'
import { cardAlertText } from '../../components/CardExpiryBanner'
import { cardBrand, day, dollars } from './format'

import styles from './AccountSettings.module.css'

const STATUS = {
  paid: 'Paid',
  payment_due: 'Payment due',
  not_paid: 'Not paid',
  voided: 'Voided',
  pending: 'Pending',
}
const pad = (n) => String(n).padStart(2, '0')

const BillingSection = ({ billing, reload }) => {
  const [invoices, setInvoices] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const cardAlert = useSelector((state) => state.user?.data?.card_alert)

  useEffect(() => {
    getInvoices()
      .then(({ data }) => setInvoices(data))
      .catch(() => setInvoices(false))
  }, [billing]) // again after any plan change, which may add a receipt

  const card = billing?.card
  // Chargebee only knows people who have paid for something
  const canEditCard = !!(card || billing?.subscription || invoices?.length)

  const replaceCard = () => {
    setBusy(true)
    setError('')
    openCardScreen(billing.chargebee_site, () => getPortalSession().then(({ data }) => data))
      .then(() => reload())
      .catch((err) => setError(apiError(err, 'Card changes are not available right now.')))
      .finally(() => setBusy(false))
  }

  const receipt = (id) => {
    setError('')
    getInvoicePdf(id)
      .then(({ data }) => {
        window.location.href = data.url
      })
      .catch((err) => setError(apiError(err, 'That receipt is not available right now.')))
  }

  return (
    <section id="billing" className={styles.card} aria-labelledby="billing-title">
      <h2 id="billing-title" className={styles.h2}>
        Payment and billing
      </h2>
      <p className={styles.lead}>Your card, and receipts for everything you have paid for.</p>

      <div className={styles.listRow}>
        <div className={styles.grow}>
          {card ? (
            <>
              <b>
                {cardBrand(card.brand)} ending {card.last4}
              </b>
              <div className={styles.small}>
                Expires {pad(card.expiry_month)}/{String(card.expiry_year).slice(-2)}, used for your
                subscription and cookbook orders
              </div>
            </>
          ) : (
            <span className={styles.small}>
              No card saved. You add one when you pay for a plan or a cookbook.
            </span>
          )}
        </div>
        {canEditCard && billing?.chargebee_site ? (
          <button className={styles.btn} disabled={busy} onClick={replaceCard}>
            {card ? 'Replace card' : 'Add a card'}
          </button>
        ) : null}
      </div>
      {card && cardAlert && cardAlert.last4 === card.last4 ? (
        <p className={styles.error} role="status">
          {cardAlertText(cardAlert)} Replace it so you keep Pro.
        </p>
      ) : null}
      <p className={styles.small}>
        Cards are typed into Chargebee's secure screen. Morsel never sees your card number.
      </p>

      <h3 className={styles.h3}>Billing history</h3>
      {invoices === null ? (
        <p className={styles.small}>Loading...</p>
      ) : invoices === false ? (
        <p className={styles.small}>Receipts are not available right now.</p>
      ) : !invoices.length ? (
        <p className={styles.small}>Nothing yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>What</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{day(i.date)}</td>
                  <td>{i.description || 'Morsel'}</td>
                  <td>{dollars(i.total)}</td>
                  <td>{STATUS[i.status] || i.status}</td>
                  <td>
                    <button
                      className={styles.btnLink}
                      style={{ minHeight: 0 }}
                      onClick={() => receipt(i.id)}
                    >
                      Receipt (PDF)
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
    </section>
  )
}

export default BillingSection
